import type { Asset, AssetLink } from "contentful";
import type { ContentImage } from "@/types/contentful/ContentImage";

/** Strip zero-width characters that Contentful injects into text fields. */
const ZERO_WIDTH_CHARS = /\u200B|\u200C|\u200D|\uFEFF/g;
export function stripZeroWidth(text: string | null | undefined): string {
  // Draft entries in preview often have unfilled fields; guard so a missing value
  // never throws (which would error the page and crash Contentful Live Preview).
  return (text ?? "").replace(ZERO_WIDTH_CHARS, "");
}

/**
 * Recursively strip zero-width characters from all strings in a Contentful
 * response object. Applied automatically via the sanitizing client wrapper.
 * Uses a WeakSet to detect and break circular references.
 */
export function sanitizeDeep<T>(value: T): T {
  return sanitizeDeepInner(value, new WeakSet());
}

function sanitizeDeepInner<T>(value: T, seen: WeakSet<object>): T {
  if (typeof value === "string") return stripZeroWidth(value) as T;
  if (value === null || value === undefined || value instanceof Date) return value;
  if (typeof value !== "object") return value;
  if (seen.has(value)) return value;
  seen.add(value);
  if (Array.isArray(value)) {
    return value.map(item => sanitizeDeepInner(item, seen)) as unknown as T;
  }
  const out = {} as T;
  for (const key of Object.keys(value)) {
    (out as Record<string, unknown>)[key] = sanitizeDeepInner((value as Record<string, unknown>)[key], seen);
  }
  return out;
}

export class ContentfulHelper {
  public static readonly getMetadata = async <
    TEntry extends { fields: { seoTitle?: string; metaDescription?: string } },
    TArgs extends unknown[],
  >(
    fetchFn: (...args: TArgs) => Promise<TEntry | null>,
    ...args: TArgs
  ): Promise<{ title: string; description: string } | null> => {
    const entry = await fetchFn(...args);
    if (!entry?.fields.seoTitle || !entry?.fields.metaDescription) return null;
    return {
      title: entry.fields.seoTitle,
      description: entry.fields.metaDescription,
    };
  };

  public static readonly parseContentfulContentImage = (
    asset?: Asset<undefined, string> | { sys: AssetLink },
  ): ContentImage | null => {
    if (!asset) {
      return null;
    }

    if (!("fields" in asset)) {
      return null;
    }

    const url = asset.fields.file?.url;
    if (!url) {
      return null;
    }
    const src = url.startsWith("//") ? `https:${url}` : url;

    return {
      src,
      alt: stripZeroWidth(asset.fields.description ?? ""),
      width: asset.fields.file?.details.image?.width ?? 0,
      height: asset.fields.file?.details.image?.height ?? 0,
    };
  };
}
